import React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@common/components';
import { FormField } from '@common/components/form/FormField';
import { Label } from '@common/components/form/Label';
import { Textarea } from '@common/components/form/Textarea';
import { Stack } from '@common/components/Stack';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from '@lang';

const RequestOrderModificationFormSchema = z.object({
  message: z.string().min(1),
});

type RequestOrderModificationFormValues = z.infer<typeof RequestOrderModificationFormSchema>;

type RequestOrderModificationFormProps = {
  siteSlug: string;
  orderUuid: string;
  isCancelling: boolean;
  cancelOrder: (values: { message: string; orderId: string; siteSlug: string }) => void;
  amendOrder: (values: { message: string; orderId: string; siteSlug: string }) => void;
  isLoading: boolean;
};

export function RequestOrderModificationForm({
  siteSlug,
  orderUuid,
  isCancelling,
  amendOrder,
  cancelOrder,
  isLoading,
  ...rest
}: RequestOrderModificationFormProps) {
  const { t } = useTranslations();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<RequestOrderModificationFormValues>({
    resolver: zodResolver(RequestOrderModificationFormSchema),
    mode: 'onChange',
  });

  return (
    <form
      {...rest}
      onSubmit={handleSubmit((data) => {
        if (isCancelling) {
          cancelOrder({
            message: data.message,
            orderId: orderUuid,
            siteSlug,
          });
        } else {
          amendOrder({
            message: data.message,
            orderId: orderUuid,
            siteSlug,
          });
        }
      })}
    >
      <fieldset disabled={isLoading}>
        <Stack>
          <FormField>
            <Label htmlFor="message" required>
              {isCancelling ? t('cancelOrderForm.amend.label') : t('amendOrderForm.amend.label')}
            </Label>

            <Textarea
              {...register('message')}
              data-testid="order-request-text-area"
              id="message"
              size="xl"
              rows={5}
              placeholder={t('amendOrderForm.amend.placeholder')}
              hasError={!!errors.message}
            />
          </FormField>

          <FormField>
            <Button isPrimary type="submit" disabled={!isValid || isLoading}>
              {isCancelling ? t('cancelOrderForm.submit.label') : t('amendOrderForm.submit.label')}
            </Button>
          </FormField>
        </Stack>
      </fieldset>
    </form>
  );
}
